<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <div class="modal-header">
        <h2>Phiếu Xuất Kho</h2>
      </div>
      <div class="modal-body">
        <div class="export-receipt">
          <!-- Thông tin chung -->
          <div class="form-header">
            <div class="form-group">
              <label>Loại phiếu</label>
              <select v-model="form.loai_phieu" name="" id="">
                <option value="">-- Chọn loại phiếu --</option>
                <option value="ban_hang">Bán hàng</option>
                <option value="noi_bo">Nội bộ</option>
              </select>
            </div>

            <div class="form-group">
              <label>Ngày xuất</label>
              <input type="date" v-model="form.export_date" />
            </div>
            <div class="form-group">
              <label>Xuất cho Sản phẩm</label>
              <input
                v-model="form.target_product_serial_imei"
                placeholder="Nhập serial/imei..."
                @input="handleInputSearchTargetProduct"
              />
              <div v-if="showDropdownTargetProduct" class="dropdown">
                <div
                  class="dropdown-item"
                  v-for="item in targetProduct"
                  :key="item?.id"
                  @click="selectedTargetProduct(item)"
                >
                  <strong
                    >{{ item.ten_hang }}
                    <span>Mã: {{ item.ma_hang }}</span>
                    <span>Serial/Imei: {{ item.serial_imei }}</span>
                  </strong>
                </div>
              </div>
            </div>
          </div>

          <!-- Thêm sản phẩm -->
          <div class="add-product">
            <div class="field product-select">
              <select v-model="selectedProductId">
                <option value="">-- Chọn sản phẩm --</option>
                <option
                  v-for="product in products"
                  :key="product.id"
                  :value="product.id"
                >
                  {{ product.ten_hang }} (Tồn: {{ product.ton_kho }})
                </option>
              </select>
            </div>
            <div class="field qty">
              <input
                type="number"
                v-model="quantity"
                min="1"
                placeholder="Số lượng"
              />
            </div>

            <button @click="addProduct" class="btn-add">Thêm</button>
          </div>

          <!-- Danh sách sản phẩm -->
          <table class="product-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Tên SP</th>
                <th>Giá</th>
                <th>Số lượng</th>
                <th>Thành tiền</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in form.items" :key="index">
                <td>{{ index + 1 }}</td>
                <td>{{ item.ten_hang }}</td>
                <td>{{ formatCurrency(item.gia_von) }}</td>
                <td>
                  <div class="quantity">
                    <input
                      type="number"
                      v-model="item.quantity"
                      min="1"
                      @input="updateTotal"
                    />
                  </div>
                </td>
                <td>{{ formatCurrency(item.gia_von * item.quantity) }}</td>
                <td>
                  <button @click="removeItem(index)">Xóa</button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Tổng tiền -->
          <div class="total">
            <h3>Tổng: {{ formatCurrency(totalAmount) }}</h3>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-save" @click="submitForm">Lưu phiếu</button>
        <button @click="$emit('close')">Đóng</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { exportReceiptApi, productApi } from "@/services/api";
import { formatCurrency } from "@/utils/helpers";
import { useToast } from "@/composables/useToast";

// ================= STATE =================
const form = ref({
  loai_phieu: "",
  export_date: new Date().toISOString().substr(0, 10),
  target_product_serial_imei: "",
  target_product_id: "",
  items: [],
});

const emit = defineEmits(["save"]);
const showDropdownTargetProduct = ref(false);
const toast = useToast();

const products = ref([]);
const targetProduct = ref(null);
const selectedProductId = ref("");
const quantity = ref(1);
const timeoutID = ref(null);

const debounceSearch = (callback, delay = 1000) => {
  if (timeoutID.value) {
    clearTimeout(timeoutID.value);
  }
  timeoutID.value = setTimeout(() => {
    callback();
  }, delay);
};

// ================= FORMAT =================

const handleInputSearchTargetProduct = async () => {
  if (!form.value.target_product_serial_imei) return;

  const targetProductSerialImei = form.value.target_product_serial_imei.replace(
    /\s+/g,
    "",
  );
  debounceSearch(async () => {
    try {
      const response = await productApi.getProductInTechnicalStock({
        search: targetProductSerialImei,
      });
      const result = response.data.data.data || response.data;

      if (Array.isArray(result) && result.length > 0) {
        targetProduct.value = [result[0]];
        showDropdownTargetProduct.value = true;
      } else {
        form.value.target_product_serial_imei = "";
        targetProduct.value = [];
        showDropdownTargetProduct.value = false;
        toast.info("Không tìm thấy sản phẩm đích");
      }
    } catch (error) {
      form.value.target_product_serial_imei = "";
      showDropdownTargetProduct.value = false;
      const errorMsg =
        error.response?.data?.error || "Lỗi tìm kiếm sản phẩm đích";
      toast.error(errorMsg);
    }
  });
};

// ================= FETCH PRODUCTS =================
const fetchProducts = async () => {
  try {
    const res = await productApi.getProductComponents();
    products.value = res.data.data.data;
  } catch (error) {
    console.error(error);
  }
};

onMounted(fetchProducts);

// ================= ADD PRODUCT =================
const addProduct = () => {
  if (!selectedProductId.value) return;
  const product = products.value.find((p) => p.id === selectedProductId.value);
  if (!product) return;

  const exist = form.value.items.find((i) => i.product_id === product.id);

  if (exist) {
    exist.quantity += quantity.value;
  } else {
    form.value.items.push({
      product_id: product.id,
      ten_hang: product.ten_hang,
      gia_von: product.gia_von,
      quantity: quantity.value,
    });
  }

  quantity.value = 1;
  selectedProductId.value = "";
};

// ================= REMOVE =================
const removeItem = (index) => {
  form.value.items.splice(index, 1);
};

// ================= TOTAL =================
const totalAmount = computed(() => {
  return form.value.items.reduce(
    (sum, item) => sum + item.gia_von * item.quantity,
    0,
  );
});

const updateTotal = () => {};

// ================= SELECTED =================

const selectedTargetProduct = (item) => {
  if (!item) return;
  form.value.target_product_id = item.id;
  form.value.target_product_serial_imei = item.serial_imei;
  showDropdownTargetProduct.value = false;
};
// ================= SUBMIT =================
const submitForm = async () => {
  const dataSend = {
    ...form.value,
    tong_gia_tri: totalAmount.value,
  };
  try {
    await exportReceiptApi.createExportReceipt(dataSend);
    toast.success("Đã tạo phiếu xuất kho & cập nhật lại giá vốn");
    emit("save");
  } catch (error) {
    const errorMsg =
      error.response?.data?.errors || "Lỗi tạo phiếu xuất kho linh kiện";
    toast.error(errorMsg);
  }
};
</script>

<style scoped>
.export-receipt {
  padding: 20px;
  background: #fff;
}

.form-header {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

/* Dropdown */
.dropdown-item {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.dropdown-item:hover {
  background-color: #d3d3d382;
  color: #2a2828;
}
.dropdown-item span {
  font-size: 0.85em;
  color: #666;
}
.dropdown-item strong {
  overflow: hidden;
  display: grid;
  text-align: left;
}

.add-product {
  display: flex;
  gap: 12px;
  align-items: center;
  background: #f8fafc;
  padding: 14px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  margin-bottom: 20px;
}

/* Field chung */
.add-product .field {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 10px;
  height: 40px;
  transition: all 0.2s ease;
}

.add-product .field:focus-within {
  border-color: #42b983;
  box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.15);
}

/* Select */
.add-product select {
  border: none;
  outline: none;
  width: 260px;
  background: transparent;
  font-size: 14px;
}

/* Quantity */
.add-product .qty input {
  border: none;
  outline: none;
  width: 70px;
  text-align: center;
  font-size: 14px;
}
.quantity {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 10px;
  height: 40px;
  transition: all 0.2s ease;
}
.quantity:focus-within {
  border-color: #42b983;
  box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.15);
}
.quantity input {
  border: none;
  outline: none;
  width: 70px;
  text-align: center;
  font-size: 14px;
}

/* Button */
.btn-add {
  height: 40px;
  padding: 0 18px;
  background: #42b983;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-add:hover {
  background: #369f6e;
  transform: translateY(-1px);
}

.btn-add:active {
  transform: scale(0.98);
}

.product-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.product-table th,
.product-table td {
  border: 1px solid #ddd;
  padding: 8px;
}

.total {
  text-align: right;
  font-weight: bold;
}

.actions {
  text-align: right;
}

.btn-save {
  background: #42b983;
  color: #fff;
  padding: 10px 20px;
  border: none;
}
</style>
